/**
 * 发票上传（经办人）
 *
 * 本页的重点不是「传文件」，而是**把查验要素讲清楚**：
 * 发票查验失败的头号原因是要素给错——专票要「不含税金额」、普票要「校验码后 6 位」、
 * 二手车要「车价合计」、数电票连发票代码都没有。因此这里直接用 @ilc/shared 的
 * VERIFY_REQUIREMENTS 动态提示「本票种需要填什么」，并提供本地预校验，
 * 避免因格式错误发起无效调用（验真通道对部分失败结果同样计费）。
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'antd';
import { InboxOutlined, InfoCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  INVOICE_TYPE_LABELS,
  InvoiceTypeCode,
  VERIFY_AMOUNT_KIND_LABELS,
  VERIFY_REQUIREMENTS,
  isEInvoice,
  precheckVerifyInput,
  type VerifyPrecheckResult,
} from '@ilc/shared';
import { contractApi, invoiceApi } from '@/api/endpoints';
import { isUnimplementedError } from '@/api/client';
import { mockContractList } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { feedback } from '@/utils/feedback';

const { Dragger } = Upload;

/** 票种下拉选项（按「传统票 / 数电票 / 其他」分组，便于快速定位） */
function invoiceTypeOptions(): Array<{ label: string; options: Array<{ label: string; value: InvoiceTypeCode }> }> {
  const entries = Object.entries(INVOICE_TYPE_LABELS) as Array<[InvoiceTypeCode, string]>;
  const e = entries.filter(([code]) => isEInvoice(code));
  const normal = entries.filter(([code]) => !isEInvoice(code) && code !== InvoiceTypeCode.UNKNOWN);
  const other = entries.filter(([code]) => code === InvoiceTypeCode.UNKNOWN);
  return [
    { label: '传统发票（有代码/校验码）', options: normal.map(([value, label]) => ({ value, label })) },
    { label: '数电票（全电发票）', options: e.map(([value, label]) => ({ value, label })) },
    { label: '其他', options: other.map(([value, label]) => ({ value, label })) },
  ];
}

/** 单票种查验要素提示 */
function RequirementPanel({ type }: { type: InvoiceTypeCode }): React.ReactElement {
  const requirement = VERIFY_REQUIREMENTS[type];
  const eInvoice = isEInvoice(type);

  return (
    <Descriptions size="small" column={1} bordered style={{ marginTop: 8 }}>
      <Descriptions.Item label="票种">
        <Space>
          <Tag color="blue">{INVOICE_TYPE_LABELS[type]}</Tag>
          <Typography.Text type="secondary" className="ilc-mono" style={{ fontSize: 12 }}>
            代码 {type}
          </Typography.Text>
          {eInvoice ? <Tag color="green">数电票：无发票代码</Tag> : null}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="发票代码">
        {requirement.needInvoiceCode ? (
          <span>
            必填
            {requirement.invoiceCodeLength ? `，${requirement.invoiceCodeLength} 位` : '，位数不固定'}
          </span>
        ) : (
          <Typography.Text type="secondary">不需要（数电票没有发票代码，填写反而会报要素不规范）</Typography.Text>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="发票号码">
        {requirement.invoiceNoLength ? `${requirement.invoiceNoLength} 位` : '位数不固定'}
      </Descriptions.Item>
      <Descriptions.Item label="金额要素">
        {requirement.amountKind === 'NONE' ? (
          <Typography.Text type="secondary">无需填写金额</Typography.Text>
        ) : (
          <Typography.Text strong style={{ color: '#d46b08' }}>
            必须填写{VERIFY_AMOUNT_KIND_LABELS[requirement.amountKind]}
          </Typography.Text>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="校验码">
        {requirement.needVerifyCode ? (
          <Typography.Text strong style={{ color: '#d46b08' }}>
            {requirement.verifyCodeHint ?? '需要填写校验码后 6 位'}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">不需要</Typography.Text>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="开票日期">
        必填，支持 2026年09月07日 / 2026-09-07 / 20260907 三种写法
      </Descriptions.Item>
    </Descriptions>
  );
}

/** 全部票种的查验要素对照表 */
function RequirementTable(): React.ReactElement {
  const rows = useMemo(
    () =>
      (Object.keys(VERIFY_REQUIREMENTS) as InvoiceTypeCode[]).map((code) => {
        const req = VERIFY_REQUIREMENTS[code];
        return {
          code,
          label: INVOICE_TYPE_LABELS[code],
          needInvoiceCode: req.needInvoiceCode,
          amountKind: VERIFY_AMOUNT_KIND_LABELS[req.amountKind],
          needVerifyCode: req.needVerifyCode,
          hint: req.verifyCodeHint ?? '',
          invoiceNoLength: req.invoiceNoLength,
          invoiceCodeLength: req.invoiceCodeLength,
        };
      }),
    [],
  );

  return (
    <Table
      size="small"
      rowKey="code"
      pagination={false}
      scroll={{ y: 320, x: 'max-content' }}
      dataSource={rows}
      columns={[
        { title: '票种', dataIndex: 'label', width: 200 },
        { title: '代码', dataIndex: 'code', width: 60, render: (v: string) => <span className="ilc-mono">{v}</span> },
        {
          title: '需要发票代码',
          dataIndex: 'needInvoiceCode',
          width: 110,
          render: (v: boolean) => (v ? <Tag color="red">需要</Tag> : <Tag color="green">不需要</Tag>),
        },
        { title: '金额口径', dataIndex: 'amountKind', width: 160 },
        {
          title: '需要校验码',
          dataIndex: 'needVerifyCode',
          width: 100,
          render: (v: boolean) => (v ? <Tag color="red">需要</Tag> : <Tag>不需要</Tag>),
        },
        { title: '号码位数', dataIndex: 'invoiceNoLength', width: 90, render: (v: number | null) => v ?? '不限' },
        { title: '校验码说明', dataIndex: 'hint', ellipsis: true },
      ]}
    />
  );
}

/** 本地预校验表单：上传前先自查要素，避免无效调用产生费用 */
function PrecheckForm(): React.ReactElement {
  const [form] = Form.useForm<{
    invoiceType: InvoiceTypeCode;
    invoiceCode?: string;
    invoiceNo: string;
    invoiceDate: string;
    amount?: string;
    verifyCode?: string;
  }>();
  const [result, setResult] = useState<VerifyPrecheckResult | null>(null);
  const [type, setType] = useState<InvoiceTypeCode>(InvoiceTypeCode.VAT_SPECIAL);
  const requirement = VERIFY_REQUIREMENTS[type];

  const runPrecheck = (): void => {
    const values = form.getFieldsValue();
    setResult(precheckVerifyInput(values));
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <SafetyCertificateOutlined />
          本地预校验（发起查验前自查要素）
        </Space>
      }
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          验真通道对「信息不一致」「查无此票」同样计费，先本地拦掉格式错误可以省钱
        </Typography.Text>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ invoiceType: InvoiceTypeCode.VAT_SPECIAL }}
        onValuesChange={(changed: { invoiceType?: InvoiceTypeCode }) => {
          setResult(null);
          if (changed.invoiceType) setType(changed.invoiceType);
        }}
      >
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="invoiceType" label="票种" rules={[{ required: true }]}>
              <Select options={invoiceTypeOptions()} showSearch optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="invoiceNo"
              label={`发票号码${requirement.invoiceNoLength ? `（${requirement.invoiceNoLength} 位）` : ''}`}
              rules={[{ required: true, message: '请输入发票号码' }]}
            >
              <Input className="ilc-mono" placeholder="如 24112000000098761234" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="invoiceCode"
              label={`发票代码${requirement.needInvoiceCode ? '（必填）' : '（该票种不需要）'}`}
            >
              <Input
                className="ilc-mono"
                placeholder={requirement.needInvoiceCode ? '如 3700191130' : '数电票留空'}
                disabled={!requirement.needInvoiceCode}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="invoiceDate" label="开票日期" rules={[{ required: true, message: '请输入开票日期' }]}>
              <Input placeholder="2026-09-07 或 20260907 或 2026年09月07日" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label={`金额${requirement.amountKind === 'NONE' ? '（该票种无需填写）' : `（${VERIFY_AMOUNT_KIND_LABELS[requirement.amountKind]}）`}`}
            >
              <Input
                className="ilc-mono"
                placeholder="如 111327.43"
                disabled={requirement.amountKind === 'NONE'}
                suffix="元"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="verifyCode"
              label="校验码"
              extra={requirement.needVerifyCode ? (requirement.verifyCodeHint ?? '填写校验码后 6 位') : '该票种不需要'}
            >
              <Input
                className="ilc-mono"
                placeholder="仅需后 6 位"
                maxLength={20}
                disabled={!requirement.needVerifyCode}
              />
            </Form.Item>
          </Col>
        </Row>

        <Space>
          <Button type="primary" onClick={runPrecheck}>
            按票种规则校验
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              setResult(null);
            }}
          >
            清空
          </Button>
        </Space>
      </Form>

      {result ? (
        <Alert
          style={{ marginTop: 12 }}
          type={result.ok ? 'success' : 'error'}
          showIcon
          message={result.ok ? '要素完整，可以发起查验' : `发现 ${result.errors.length} 处问题，请先修正再发起查验`}
          description={
            result.ok ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                规范化结果：号码 {result.normalized.invoiceNo} · 日期 {result.normalized.invoiceDate}
                {result.normalized.amount ? ` · 金额 ${result.normalized.amount} 元` : ''}
                {result.normalized.verifyCode ? ` · 校验码后 6 位 ${result.normalized.verifyCode}` : ''}
              </Typography.Text>
            ) : (
              <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
                {result.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )
          }
        />
      ) : null}
    </Card>
  );
}

export default function InvoiceUpload(): React.ReactElement {
  const navigate = useNavigate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [knownType, setKnownType] = useState<InvoiceTypeCode | undefined>();
  const [contractNo, setContractNo] = useState<string | undefined>();
  const [remark, setRemark] = useState('');
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  const contracts = useResource(() => contractApi.list({ page: 1, pageSize: 50 }), {
    fallback: () => mockContractList({ page: 1, pageSize: 50 }),
  });

  const hintType = knownType ?? InvoiceTypeCode.UNKNOWN;

  const props: UploadProps = {
    multiple: true,
    accept: '.pdf,.ofd,.png,.jpg,.jpeg,.xml',
    fileList,
    showUploadList: {
      showRemoveIcon: true,
      showPreviewIcon: false,
      showDownloadIcon: false,
    },
    onChange: (info) => setFileList(info.fileList),
    beforeUpload: (file) => {
      const tooBig = file.size / 1024 / 1024 > 20;
      if (tooBig) {
        feedback.error(`${file.name} 超过 20MB，请压缩后再上传`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      const target = file as File;
      try {
        const res = await invoiceApi.upload(
          target,
          {
            ...(contractNo ? { contractNo } : {}),
            ...(knownType ? { invoiceType: knownType } : {}),
            ...(remark ? { remark } : {}),
          },
          () => undefined,
        );
        onSuccess?.(res);
        feedback.success(`${target.name} 已受理，校验在后台异步执行`);
      } catch (err) {
        const reason = err instanceof Error ? err.message : '上传失败';
        onError?.(err instanceof Error ? err : new Error(reason));
        if (isUnimplementedError(err)) {
          setBackendUnavailable(true);
          feedback.warning(`后端上传接口尚未就绪，${target.name} 未保存`);
        }
      }
    },
  };

  const uploadedIds = fileList
    .filter((f) => f.status === 'done')
    .map((f) => (f.response as { invoiceId?: string } | undefined)?.invoiceId)
    .filter((id): id is string => Boolean(id));

  return (
    <div className="ilc-page">
      <PageHeader
        title="上传发票"
        description="支持 PDF、OFD、图片与 XML 批量上传；上传后由后台异步执行 OCR 识别 → 企查查主体校验 → 税务局验真 → 合同比对，完成后进入财务初审。"
        extra={
          <Button onClick={() => navigate('/business/invoices')}>返回我的发票</Button>
        }
      />

      {backendUnavailable ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="后端上传接口尚未就绪（/api/invoices 返回 404）"
          description="页面交互与要素校验规则均可正常体验，但文件不会被保存，发票列表也不会新增记录。接口上线后本页无需改动即可正常上传。"
          action={
            <Button size="small" onClick={() => navigate('/business/invoices')}>
              查看列表
            </Button>
          }
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={11}>
          <Card size="small" title="上传发票原件">
            <Dragger {...props} style={{ padding: '12px 0' }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传（支持批量）</p>
              <p className="ant-upload-hint">
                支持 PDF / OFD / PNG / JPG / XML，单个文件不超过 20MB。
                同一张发票重复上传会按文件内容哈希幂等去重，不会重复计费。
              </p>
            </Dragger>

            <Form layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item
                label="关联合同（建议填写）"
                extra="关联后校验报告才能做合同金额与品类比对；未关联属于低风险提示，不阻断流程"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="按合同编号 / 名称搜索"
                  value={contractNo}
                  onChange={(value) => setContractNo(value)}
                  loading={contracts.loading}
                  options={(contracts.data?.list ?? []).map((c) => ({
                    value: c.contractNo,
                    label: `${c.contractNo} · ${c.name}`,
                  }))}
                  notFoundContent={
                    contracts.loading ? '加载中…' : '合同接口未就绪或暂无合同，可留空'
                  }
                />
              </Form.Item>

              <Form.Item
                label="已知票种（可选）"
                extra="留空由 OCR 识别判定；若识别易错（如数电纸质票），可在此指定以提高准确率"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="留空 = 由 OCR 判定"
                  value={knownType}
                  onChange={(value) => setKnownType(value)}
                  options={invoiceTypeOptions()}
                />
              </Form.Item>

              <Form.Item label="备注（可选）">
                <Input.TextArea
                  rows={2}
                  maxLength={200}
                  showCount
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="如：项目部 8 月钢材款，发票原件已寄回"
                />
              </Form.Item>
            </Form>

            {uploadedIds.length > 0 ? (
              <Alert
                type="success"
                showIcon
                message={`已受理 ${uploadedIds.length} 张发票`}
                description="校验结果为异步返回，可在「我的发票」中查看进度与风险标记。"
                action={
                  <Button size="small" type="primary" onClick={() => navigate('/business/invoices')}>
                    查看进度
                  </Button>
                }
              />
            ) : null}
          </Card>

          <Card size="small" title="上传须知" style={{ marginTop: 16 }}>
            <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 2, fontSize: 13 }}>
              <li>上传是异步受理：接口立即返回受理结果，校验在后台执行，失败会自动重试。</li>
              <li>校验只做风险标记，不会自动放行，也不会自动生成凭证。</li>
              <li>红冲、作废、供应商异常等高风险发票默认阻断流转 T+，需财务特例放行并留痕。</li>
              <li>外部接口不可用时会标记「外部校验暂不可用」，可由人工核验后接管，不卡死业务。</li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} xl={13}>
          <Card
            size="small"
            title={
              <Space>
                <InfoCircleOutlined style={{ color: '#1677ff' }} />
                本票种查验要素提示
              </Space>
            }
            extra={
              <Select
                size="small"
                style={{ width: 240 }}
                value={hintType}
                onChange={(value) => setKnownType(value)}
                showSearch
                optionFilterProp="label"
                options={invoiceTypeOptions()}
              />
            }
          >
            {hintType === InvoiceTypeCode.UNKNOWN ? (
              <Alert
                type="info"
                showIcon
                message="未指定票种"
                description="选择票种后将显示该票种查验时必须提供哪些要素。这是查验失败最常见的原因：专票要填不含税金额，普票要填校验码后 6 位，二手车要填车价合计，数电票没有发票代码。"
              />
            ) : (
              <RequirementPanel type={hintType} />
            )}
          </Card>

          <div style={{ marginTop: 16 }}>
            <PrecheckForm />
          </div>

          <Collapse
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'all',
                label: '全部票种查验要素对照表（点击展开）',
                children: (
                  <PageState loading={false}>
                    <RequirementTable />
                    <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                      规则来自 @ilc/shared 的 VERIFY_REQUIREMENTS，前后端共用同一份定义：
                      前端提示「该填什么」，后端在调用验真接口前做同样的本地预校验。
                    </Typography.Paragraph>
                  </PageState>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
}
